import { resolveBoundsForDay, type OpenPeriod } from "@/lib/geo/timezone";

// ISO 8601 weekday index: 1=Mon … 7=Sun.
const DAY_ABBR = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_FULL = [
  "",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** Collapse a set of weekday indexes into contiguous [start, end] runs. */
function dayRuns(days: number[]): [number, number][] {
  const uniq = [...new Set(days)].sort((a, b) => a - b);
  const runs: [number, number][] = [];
  if (uniq.length === 0) return runs;

  let start = uniq[0];
  let prev = uniq[0];
  for (let i = 1; i < uniq.length; i++) {
    if (uniq[i] === prev + 1) {
      prev = uniq[i];
    } else {
      runs.push([start, prev]);
      start = prev = uniq[i];
    }
  }
  runs.push([start, prev]);
  return runs;
}

/** [1,2,3,4,5] → "Mon–Fri"; [6,7] → "Sat–Sun"; [1,3] → "Mon, Wed". */
export function formatDays(days: number[]): string {
  return dayRuns(days)
    .map(([a, b]) => (a === b ? DAY_ABBR[a] : `${DAY_ABBR[a]}–${DAY_ABBR[b]}`))
    .join(", ");
}

/** Like {@link formatDays} but spelled out: [1,2,3,4,5] → "Monday – Friday". */
export function formatDaysLong(days: number[]): string {
  return dayRuns(days)
    .map(([a, b]) => (a === b ? DAY_FULL[a] : `${DAY_FULL[a]} – ${DAY_FULL[b]}`))
    .join(", ");
}

/** "15:00:00" → "3 PM"; "15:30" → "3:30 PM"; null → "close". */
export function formatTime(t: string | null): string {
  if (!t) return "close";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr ?? "0", 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return m === 0 ? `${h} ${ampm}` : `${h}:${String(m).padStart(2, "0")} ${ampm}`;
}

/**
 * Render an HH window for display. All-day windows always render as "Open to close" —
 * never as a time-prefixed string. Time-windowed rows reuse {@link formatTime} for
 * each side and join with an en-dash; a null endTime becomes "close". An "open until X"
 * window (null start, known end — starts at the venue's open time) renders "Until <end>".
 */
export function formatWindow(window: {
  allDay: boolean;
  startTime: string | null;
  endTime: string | null;
}): string {
  if (window.allDay) return "Open to close";
  if (window.startTime === null) {
    // "open until X": only the end is published; the start is the venue's open time.
    return window.endTime ? `Until ${formatTime(window.endTime)}` : "Open to close";
  }
  return `${formatTime(window.startTime)} – ${formatTime(window.endTime)}`;
}

export function formatPrice(cents: number | null, currency = "USD"): string | null {
  if (cents == null) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

/**
 * Render a window per day, grouping days that share identical resolved bounds. Each
 * day's open-ended side is resolved against `hours` (via resolveBoundsForDay); days
 * that can't resolve fall back to {@link formatWindow}'s text. Days are grouped by the
 * resulting bounds string so e.g. Mon–Thu (close 10 PM) and Fri (close 12 AM) become
 * two lines. Returns raw day arrays so callers choose the day formatter.
 */
export function formatWindowByDay(
  window: {
    allDay: boolean;
    startTime: string | null;
    endTime: string | null;
    daysOfWeek: number[];
  },
  hours: OpenPeriod[] | null | undefined,
): { days: number[]; bounds: string }[] {
  const days = [...new Set(window.daysOfWeek)].sort((a, b) => a - b);
  const byBounds = new Map<string, number[]>();
  const order: string[] = [];
  for (const d of days) {
    const resolved = resolveBoundsForDay(window, hours, d);
    const bounds = resolved
      ? formatWindow({ allDay: false, startTime: resolved.startTime, endTime: resolved.endTime })
      : formatWindow(window);
    if (!byBounds.has(bounds)) {
      byBounds.set(bounds, []);
      order.push(bounds);
    }
    byBounds.get(bounds)!.push(d);
  }
  return order.map((bounds) => ({ days: byBounds.get(bounds)!, bounds }));
}
